import React from 'react';
import { Work, Construction } from '@mui/icons-material';
import { PageContainer, Content } from '../PageTemplate';
import Form from '../../components/form';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const GradientHero = styled.div`
  background: linear-gradient(
    150deg,
    #ff80b5 0%,
    #9089fc 20%,
    #ff80b5 40%,
    #40c9ff 60%,
    #a5fecb 80%,
    #9089fc 100%
  );
  background-size: 200% 200%;
  animation: gradientMove 15s ease infinite;
  padding: 16rem 2rem 8rem 2rem;
  margin-top: -80px;
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;

  @keyframes gradientMove {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at 50% 50%,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 70%
    );
    filter: blur(60px);
    opacity: 0.6;
  }

  @media (max-width: 768px) {
    padding: 12rem 1rem 6rem 1rem;
    
    h1 {
      font-size: 2.5rem !important;
    }
    
    p {
      font-size: 1rem !important;
      padding: 0 1rem;
    }
  }
`;

const CareersSection = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 4rem 2rem;
  text-align: center;

  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const CareerText = styled(motion.p)`
  font-size: 1.25rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.text};
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const DevelopmentBanner = styled.div`
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: white;
  text-align: center;
  padding: 0.75rem;
  position: relative;
  overflow: hidden;
  font-weight: 500;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url('/noise.png') repeat;
    opacity: 0.1;
    mix-blend-mode: overlay;
  }

  span {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const Careers = () => {
  return (
    <PageContainer>
      <GradientHero>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Work sx={{ 
            fontSize: 64, 
            marginBottom: 2,
            filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))'
          }} />
          <motion.h1
            style={{
              fontSize: '3.5rem',
              marginBottom: '1.5rem',
              fontWeight: 700,
              position: 'relative',
              zIndex: 2
            }}
          >
            Careers
          </motion.h1>
          <motion.p
            style={{
              fontSize: '1.25rem',
              maxWidth: '800px',
              margin: '0 auto',
              opacity: 0.9,
              lineHeight: 1.6,
              position: 'relative',
              zIndex: 2
            }}
          >
            Join our growing team
          </motion.p>
        </motion.div>
      </GradientHero>

      <DevelopmentBanner>
        <span>
          <Construction sx={{ fontSize: 20 }} />
          Our careers page is under development. Check back soon for exciting opportunities!
        </span>
      </DevelopmentBanner>

      <Content>
        <CareersSection>
          <CareerText
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            At ServTrackr, we're building the future of service business management. We're looking for passionate individuals who want to make a real impact in the industry and help businesses thrive in the digital age.
          </CareerText>
          
          <CareerText
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            We offer competitive compensation, flexible remote work options, comprehensive benefits, and a culture that values innovation, collaboration, and work-life balance. Join us in our mission to revolutionize how service businesses operate.
          </CareerText>
        </CareersSection>

        <Form />
      </Content>
    </PageContainer>
  );
};

export default Careers;